
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { PlusIcon, SearchIcon, FilterIcon, TrendingUpIcon, ClockIcon, CheckCircleIcon, AlertTriangleIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getApiEndpoint } from "@/lib/api";
import { getToken, getUser, clearAuth } from "@/lib/auth-storage";
import { useLanguage } from "@/contexts/LanguageContext";

interface Ticket {
  id: string;
  title: string;
  description: string;
  type: string;
  category: string;
  subcategory?: string;
  priority: string;
  status: string;
  requester: { id: number; name: string; email: string } | null;
  assignee: { id: number; name: string; email: string } | null;
  company: { id: number; name: string; domain: string } | null;
  sla_remaining: string;
  created_at: string;
  updated_at: string;
}

const ITSMDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    const checkUserAndFetch = () => {
      const user = getUser();
      if (!user) {
        navigate("/login");
        return;
      }

      try {
        const parsedUser = JSON.parse(user);
        // Redirecionar usuários internos para a tela de atendimento
        if (parsedUser.role === "internal" || parsedUser.role === "analyst" || parsedUser.role === "admin") {
          navigate("/atendimento");
          return;
        }
      } catch (error) {
        console.error("Erro ao parsear usuário:", error);
      }

      fetchTickets();
    };

    checkUserAndFetch();

    // Listener para mudanças de usuário
    const handleUserChange = () => {
      checkUserAndFetch();
    };

    window.addEventListener("userChanged", handleUserChange);

    return () => {
      window.removeEventListener("userChanged", handleUserChange);
    };
  }, [navigate]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(getApiEndpoint("/api/tickets"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      } else if (response.status === 401) {
        clearAuth();
        navigate("/login");
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || t.itsmDashboard.error);
      }
    } catch (error: any) {
      setError(t.itsmDashboard.connectionError);
    } finally {
      setLoading(false);
    }
  };

  const convertStatusFromBackend = (status: string) => {
    switch (status) {
      case "open": return t.dashboard.statusOpen;
      case "in_progress": return t.dashboard.statusInProgress;
      case "waiting_response": return t.dashboard.statusWaiting;
      case "resolved": return t.dashboard.statusResolved;
      case "closed": return t.dashboard.statusClosed;
      default: return status;
    }
  };

  const convertPriorityFromBackend = (priority: string) => {
    switch (priority) {
      case "critical": return t.dashboard.priorityCritical;
      case "high": return t.dashboard.priorityHigh;
      case "medium": return t.dashboard.priorityMedium;
      case "low": return t.dashboard.priorityLow;
      default: return priority;
    }
  };

  const convertTypeFromBackend = (type: string) => {
    switch (type) {
      case "incident": return t.dashboard.typeIncident;
      case "request": return t.dashboard.typeRequest;
      case "problem": return t.dashboard.typeProblem;
      case "change": return t.dashboard.typeChange;
      default: return type;
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || ticket.status === statusFilter;
    const matchesPriority = priorityFilter === "all" || ticket.priority === priorityFilter;
    const matchesType = typeFilter === "all" || ticket.type === typeFilter;
    return matchesSearch && matchesStatus && matchesPriority && matchesType;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "open": return "bg-blue-100 text-blue-800";
      case "in_progress": return "bg-yellow-100 text-yellow-800";
      case "waiting_response": return "bg-orange-100 text-orange-800";
      case "resolved": return "bg-green-100 text-green-800";
      case "closed": return "bg-gray-100 text-gray-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "critical": return "bg-red-100 text-red-800";
      case "high": return "bg-orange-100 text-orange-800";
      case "medium": return "bg-yellow-100 text-yellow-800";
      case "low": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "incident": return <AlertTriangleIcon className="h-4 w-4 text-red-600" />;
      case "request": return <CheckCircleIcon className="h-4 w-4 text-blue-600" />;
      case "problem": return <TrendingUpIcon className="h-4 w-4 text-purple-600" />;
      case "change": return <ClockIcon className="h-4 w-4 text-green-600" />;
      default: return <ClockIcon className="h-4 w-4 text-gray-600" />;
    }
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return "";
    return new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  };

  const statsData = {
    total: tickets.length,
    open: tickets.filter(t => t.status === "open").length,
    inProgress: tickets.filter(t => t.status === "in_progress").length,
    resolved: tickets.filter(t => t.status === "resolved").length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t.itsmDashboard.loading}</p>
        </div>
      </div>
    );
  }

  if (error && tickets.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="flex items-center text-red-600">
                <AlertTriangleIcon className="h-5 w-5 mr-2" />
                {t.itsmDashboard.error}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={fetchTickets}>
                {t.itsmDashboard.tryAgain}
              </Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{t.itsmDashboard.title}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t.itsmDashboard.subtitle}</p>
          </div>
          <Link to="/novo-chamado">
            <Button className="bg-blue-600 hover:bg-blue-700">
              <PlusIcon className="h-4 w-4 mr-2" />
              {t.itsmDashboard.newTicket}
            </Button>
          </Link>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.itsmDashboard.totalTickets}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{statsData.total}</div>
            </CardContent>
          </Card>
          
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.dashboard.open}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{statsData.open}</div>
            </CardContent>
          </Card>
          
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.dashboard.inProgress}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{statsData.inProgress}</div>
            </CardContent>
          </Card>
          
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">{t.dashboard.resolved}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{statsData.resolved}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filtros */}
        <Card className="mb-8 dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="flex items-center">
              <FilterIcon className="h-5 w-5 mr-2" />
              {t.itsmDashboard.filtersTitle}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={t.itsmDashboard.searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t.dashboard.status} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.dashboard.statusAll}</SelectItem>
                  <SelectItem value="open">{t.dashboard.statusOpen}</SelectItem>
                  <SelectItem value="in_progress">{t.dashboard.statusInProgress}</SelectItem>
                  <SelectItem value="waiting_response">{t.dashboard.statusWaiting}</SelectItem>
                  <SelectItem value="resolved">{t.dashboard.statusResolved}</SelectItem>
                  <SelectItem value="closed">{t.dashboard.statusClosed}</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t.dashboard.priority} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.dashboard.priorityAll}</SelectItem>
                  <SelectItem value="critical">{t.dashboard.priorityCritical}</SelectItem>
                  <SelectItem value="high">{t.dashboard.priorityHigh}</SelectItem>
                  <SelectItem value="medium">{t.dashboard.priorityMedium}</SelectItem>
                  <SelectItem value="low">{t.dashboard.priorityLow}</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t.dashboard.type} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.dashboard.typeAll}</SelectItem>
                  <SelectItem value="incident">{t.dashboard.typeIncident}</SelectItem>
                  <SelectItem value="request">{t.dashboard.typeRequest}</SelectItem>
                  <SelectItem value="problem">{t.dashboard.typeProblem}</SelectItem>
                  <SelectItem value="change">{t.dashboard.typeChange}</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="outline" onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
                setPriorityFilter("all");
                setTypeFilter("all");
              }}>
                {t.dashboard.clearFilters}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Lista de Chamados */}
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle>{t.user.myTickets}</CardTitle>
            <CardDescription>
              {filteredTickets.length} {t.dashboard.ticketsList.toLowerCase()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredTickets.map((ticket) => {
                const ticketStatus = convertStatusFromBackend(ticket.status);
                const ticketPriority = convertPriorityFromBackend(ticket.priority);
                const ticketType = convertTypeFromBackend(ticket.type);
                
                return (
                  <Link key={ticket.id} to={`/chamado/${ticket.id}`}>
                    <div className="border rounded-lg p-4 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 cursor-pointer">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {getTypeIcon(ticket.type)}
                            <span className="font-semibold text-gray-900 dark:text-white">#{ticket.id}</span>
                            <Badge className={getStatusColor(ticket.status)}>{ticketStatus}</Badge>
                            <Badge className={getPriorityColor(ticket.priority)}>{ticketPriority}</Badge>
                          </div>
                          <h3 className="font-medium text-gray-900 dark:text-white mb-1">{ticket.title}</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">{ticket.description}</p>
                          <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <span>{t.dashboard.category}: {ticket.category}</span>
                            <span>•</span>
                            <span>{t.dashboard.created}: {formatDateTime(ticket.created_at)}</span>
                            <span>•</span>
                            <span>SLA: {ticket.sla_remaining}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600 dark:text-gray-400">{t.dashboard.assigneeLabel}</div>
                          <div className="font-medium text-gray-900 dark:text-white">{ticket.assignee?.name || t.dashboard.notAssigned}</div>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
              
              {filteredTickets.length === 0 && (
                <div className="text-center py-12">
                  <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t.itsmDashboard.noTickets}</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">{t.itsmDashboard.noTicketsDesc}</p>
                  <Link to="/novo-chamado">
                    <Button>
                      <PlusIcon className="h-4 w-4 mr-2" />
                      {t.itsmDashboard.openTicket}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default ITSMDashboard;
